import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Input, Spin, message, Empty, Tag, Button, Typography, Tooltip,
} from 'antd';
import {
  SearchOutlined, SwapOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import Fuse from 'fuse.js';
import * as api from 'util/api';
import './Cin7SkuMatcher.less';

const { Text } = Typography;

const FUSE_OPTIONS = {
  keys: [
    { name: 'cin7Sku', weight: 0.25 },
    { name: 'name', weight: 0.55 },
    { name: 'category', weight: 0.1 },
    { name: 'barcode', weight: 0.1 },
  ],
  threshold: 0.45,
  distance: 200,
  includeScore: true,
  minMatchCharLength: 1,
};

const MAX_SUGGESTIONS = 8;
const SCORE_HIGH = 0.2;
const SCORE_POSSIBLE = 0.4;

/**
 * Cin7 supplier SKU code from a cached product (e.g. "PW 330 Slab").
 * camelCase: cin7Sku; snake_case: cin7_sku.
 */
function getProductSku(product) {
  if (!product) return '';
  const sku = product.cin7Sku ?? product.cin7_sku ?? product.SKU ?? '';
  return typeof sku === 'string' ? sku : String(sku);
}

/** Get Cin7/DEAR id from product (camelized or snake_case). */
function getProductDearId(product) {
  if (!product) return null;
  const id = product.cin7Id ?? product.cin7_id ?? product.ID ?? product.Id;
  return id != null ? id : null;
}

/** Exclude Cin7 products whose name or SKU contains these phrases (case-insensitive). */
const EXCLUDED_PHRASES = /\(empty\)|label/i;
const KEG_PHRASE = /keg/i;

function shouldExcludeSuggestion(product, containerType) {
  const name = (product.name || '').toLowerCase();
  const sku = ((product.cin7Sku ?? product.cin7_sku) || '').toLowerCase();
  if (EXCLUDED_PHRASES.test(name) || EXCLUDED_PHRASES.test(sku)) return true;
  const isKegSku = (containerType || '').toLowerCase() === 'keg';
  if (isKegSku) {
    return !KEG_PHRASE.test(name) && !KEG_PHRASE.test(sku);
  }
  if (KEG_PHRASE.test(name) || KEG_PHRASE.test(sku)) return true;
  return false;
}

function getConfidenceLabel(score) {
  if (score <= SCORE_HIGH) return { label: 'High confidence', color: '#52c41a' };
  if (score <= SCORE_POSSIBLE) return { label: 'Possible match', color: '#faad14' };
  return { label: 'Low confidence', color: '#ff4d4f' };
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

const Cin7SkuMatcher = ({
  value,
  onChange,
  onSelectMatch,
  parentProductName,
  matrixSkuName,
  containerType,
  unitType,
  packType,
  caseTypeLabel,
  unitTypeLabel,
  compact,
  searchOnly,
  /** undefined = fetch cache locally; null = parent loading; array = use as Cin7 cache (single shared load) */
  sharedCachedProducts,
}) => {
  const [cachedProducts, setCachedProducts] = useState(
    () => (Array.isArray(sharedCachedProducts) ? sharedCachedProducts : []),
  );
  const [loadingCache, setLoadingCache] = useState(
    () => !Array.isArray(sharedCachedProducts),
  );
  const [searchInput, setSearchInput] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  /** Selected suggestion shown at once if the form value lags */
  const [pendingSku, setPendingSku] = useState(null);
  const mainInputRef = useRef(null);

  /** Query for auto-suggestions: full Matrix SKU name when set, else contextual fallback. */
  const autoSearchQuery = useMemo(() => {
    const q = (matrixSkuName || '').trim();
    if (q) return q;
    const parts = [
      parentProductName,
      caseTypeLabel,
      unitTypeLabel,
      containerType,
      unitType,
      packType,
    ].filter(Boolean);
    return parts.join(' ');
  }, [
    matrixSkuName,
    parentProductName,
    caseTypeLabel,
    unitTypeLabel,
    containerType,
    unitType,
    packType,
  ]);

  const fuse = useMemo(() => {
    if (!cachedProducts.length) return null;
    return new Fuse(cachedProducts, FUSE_OPTIONS);
  }, [cachedProducts]);

  const autoSuggestions = useMemo(() => {
    if (!fuse) return [];
    const searchQuery = autoSearchQuery.trim();
    if (!searchQuery) return [];

    const matrixQuery = (matrixSkuName || '').trim();
    const fuseResults = fuse.search(searchQuery)
      .filter((r) => r.score <= 0.6 && !shouldExcludeSuggestion(r.item, containerType));

    const suggestionKey = (p) => {
      const id = getProductDearId(p);
      if (id != null && id !== '') return `id:${id}`;
      return `sku:${getProductSku(p)}`;
    };

    const exactProduct = matrixQuery
      ? cachedProducts.find(
        (p) => (p.name || '').toLowerCase() === matrixQuery.toLowerCase()
          && !shouldExcludeSuggestion(p, containerType),
      )
      : null;

    const out = [];
    const seen = new Set();

    if (exactProduct) {
      seen.add(suggestionKey(exactProduct));
      out.push({ ...exactProduct, matchScore: 0 });
    }

    fuseResults.forEach((r) => {
      if (out.length >= MAX_SUGGESTIONS) return;
      const k = suggestionKey(r.item);
      if (seen.has(k)) return;
      seen.add(k);
      out.push({ ...r.item, matchScore: r.score });
    });

    return out.slice(0, MAX_SUGGESTIONS);
  }, [fuse, autoSearchQuery, matrixSkuName, cachedProducts, containerType]);

  const manualSearchResults = useMemo(() => {
    if (!fuse || !searchInput.trim()) return [];
    const results = fuse.search(searchInput.trim());
    return results
      .filter((r) => !shouldExcludeSuggestion(r.item, containerType))
      .slice(0, MAX_SUGGESTIONS)
      .map((r) => ({ ...r.item, matchScore: r.score }));
  }, [fuse, searchInput, containerType]);

  const applyProductList = (list) => {
    setCachedProducts(list);
    const latest = list.length
      ? list.reduce((acc, p) => {
        const at = p.lastSyncedAt ?? p.last_synced_at;
        const t = at ? new Date(at).getTime() : 0;
        return t > (acc || 0) ? t : acc;
      }, null)
      : null;
    setLastSyncedAt(latest ? new Date(latest).toISOString() : null);
  };

  const loadCache = async () => {
    setLoadingCache(true);
    try {
      const products = await api.getCachedCin7Products();
      const list = Array.isArray(products) ? products : [];
      applyProductList(list);
    } catch (err) {
      message.error('Failed to load Cin7 product cache');
      setCachedProducts([]);
    } finally {
      setLoadingCache(false);
    }
  };

  useEffect(() => {
    if (sharedCachedProducts === null) {
      setLoadingCache(true);
      return;
    }
    if (sharedCachedProducts !== undefined) {
      const list = Array.isArray(sharedCachedProducts) ? sharedCachedProducts : [];
      applyProductList(list);
      setLoadingCache(false);
      return;
    }
    loadCache();
  }, [sharedCachedProducts]);

  // When form value catches up (or user clears), clear pending so we show form value
  useEffect(() => {
    if (pendingSku !== null && (value ?? '') === pendingSku) {
      setPendingSku(null);
    }
  }, [value, pendingSku]);

  const handleSelect = (product) => {
    const sku = getProductSku(product);
    const dearCode = getProductDearId(product);
    setPendingSku(sku);
    const inputEl = mainInputRef.current?.input ?? mainInputRef.current;
    if (inputEl && typeof inputEl.value !== 'undefined') {
      inputEl.value = sku;
    }
    const eventLike = { target: { value: sku } };
    onChange?.(eventLike);
    const payload = { ...product, cin7Sku: sku, cin7Id: dearCode };
    onSelectMatch?.(payload);
    setSearchInput('');
  };

  const displayValue = pendingSku !== null ? pendingSku : (value ?? '');
  const hasMatchedCin7Sku = String(displayValue ?? '').trim().length > 0;

  const matchedProduct = useMemo(() => {
    if (!hasMatchedCin7Sku || !cachedProducts.length) return null;
    const dv = String(displayValue).trim().toLowerCase();
    return cachedProducts.find(
      (p) => getProductSku(p).toLowerCase() === dv,
    ) || null;
  }, [hasMatchedCin7Sku, displayValue, cachedProducts]);

  const displaySuggestions = searchInput.trim() ? manualSearchResults : autoSuggestions;
  const showSuggestions = displaySuggestions.length > 0 && !loadingCache && !hasMatchedCin7Sku;

  const handleClear = () => {
    setPendingSku(null);
    onChange?.({ target: { value: '' } });
    setSearchInput('');
  };

  const rowWrap = {
    width: '100%',
    minWidth: 0,
    borderBottom: '1px solid #f0f0f0',
    padding: compact ? '6px 0' : '10px 0',
  };
  const rowInner = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: compact ? 6 : 12,
  };
  const mainBlock = {
    flex: '1 1 180px',
    minWidth: 0,
  };

  if (loadingCache && cachedProducts.length === 0) {
    return (
      <div className="cin7-sku-matcher">
        <Spin size="small" />
        <span style={{ marginLeft: 8 }}>Loading Cin7 cache...</span>
      </div>
    );
  }

  if (compact && searchOnly) {
    return (
      <div className="cin7-sku-matcher cin7-sku-matcher--compact">
        {hasMatchedCin7Sku ? (
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
          }}
          >
            <Text strong style={{ wordBreak: 'break-word' }}>
              {matchedProduct?.name || 'Cin7 Product'}
            </Text>
            <Tag color="blue">{displayValue}</Tag>
            <Button
              size="small"
              icon={<SwapOutlined />}
              onClick={handleClear}
            >
              Change
            </Button>
          </div>
        ) : null}
        {!hasMatchedCin7Sku && cachedProducts.length > 0 ? (
          <>
            <div className="cin7-sku-matcher-search">
              <Input
                placeholder="Search Cin7 products..."
                prefix={<SearchOutlined />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                allowClear
                size="small"
              />
            </div>
            {showSuggestions ? (
              <ul className="cin7-sku-matcher-suggestions">
                {displaySuggestions.map((product, index) => {
                  const rowSku = getProductSku(product);
                  return (
                    <li key={rowSku || getProductDearId(product) || index}>
                      <button
                        type="button"
                        className="cin7-sku-matcher-suggestion-item"
                        onClick={() => handleSelect(product)}
                      >
                        <span className="cin7-sku-matcher-sku">{rowSku || '—'}</span>
                        <span className="cin7-sku-matcher-name">{product.name || '—'}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </>
        ) : null}
        {!hasMatchedCin7Sku && cachedProducts.length === 0 ? (
          <Text type="secondary">Cin7 cache is empty.</Text>
        ) : null}
      </div>
    );
  }

  if (compact) {
    return (
      <div className="cin7-sku-matcher cin7-sku-matcher--compact">
        <div className="cin7-sku-matcher-input-row">
          <Input
            ref={mainInputRef}
            value={displayValue}
            size="small"
            onChange={(e) => {
              setPendingSku(null);
              onChange?.(e);
            }}
            placeholder="Cin7 SKU or browse…"
            allowClear
            style={{ flex: 1 }}
          />
        </div>
        {cachedProducts.length > 0 && !hasMatchedCin7Sku && (
          <>
            <div className="cin7-sku-matcher-search">
              <Input
                placeholder="Search Cin7 products..."
                prefix={<SearchOutlined />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                allowClear
                size="small"
              />
            </div>
            {showSuggestions && (
              <ul className="cin7-sku-matcher-suggestions">
                {displaySuggestions.map((product, index) => {
                  const rowSku = getProductSku(product);
                  return (
                    <li key={rowSku || getProductDearId(product) || index}>
                      <button
                        type="button"
                        className="cin7-sku-matcher-suggestion-item"
                        onClick={() => handleSelect(product)}
                      >
                        <span className="cin7-sku-matcher-sku">{rowSku || '—'}</span>
                        <span className="cin7-sku-matcher-name">{product.name || '—'}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="cin7-sku-matcher" style={{ width: '100%', minWidth: 0 }}>
      {/* Hidden input to keep form value in sync */}
      <Input
        ref={mainInputRef}
        value={displayValue}
        onChange={(e) => {
          setPendingSku(null);
          onChange?.(e);
        }}
        style={{ display: 'none' }}
      />

      {hasMatchedCin7Sku ? (
        <div style={rowWrap}>
          <div style={rowInner}>
            <div style={mainBlock}>
              <div style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
              }}
              >
                <Text strong style={{ wordBreak: 'break-word' }}>
                  {matchedProduct?.name || 'Cin7 Product'}
                </Text>
                <Tag color="blue">{displayValue}</Tag>
              </div>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px 12px',
                alignItems: 'baseline',
                fontSize: 12,
                color: 'rgba(0, 0, 0, 0.45)',
                marginTop: 6,
              }}
              >
                {matchedProduct && getProductDearId(matchedProduct) && (
                  <span style={{ whiteSpace: 'nowrap' }}>
                    ID:
                    {' '}
                    {getProductDearId(matchedProduct)}
                  </span>
                )}
                {matchedProduct?.category && (
                  <span style={{ wordBreak: 'break-word' }}>
                    {matchedProduct.category}
                  </span>
                )}
                <Tag
                  icon={<CheckCircleOutlined />}
                  color="success"
                  style={{ fontSize: 11, lineHeight: '18px' }}
                >
                  Matched
                </Tag>
              </div>
            </div>
            <Button
              size="small"
              icon={<SwapOutlined />}
              onClick={handleClear}
            >
              Change
            </Button>
          </div>
        </div>
      ) : (
        <>
          {cachedProducts.length === 0 ? (
            <div className="cin7-sku-matcher-empty-cache">
              <Empty
                description="Cin7 cache is empty. Sync under Product Database → Product Cache → Cin7 Sync."
              />
            </div>
          ) : (
            <>
              <div className="cin7-sku-matcher-search" style={{ marginBottom: 8 }}>
                <Input
                  placeholder="Search Cin7 products by SKU, name, category..."
                  prefix={<SearchOutlined />}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  allowClear
                />
              </div>

              {autoSearchQuery.trim() && !searchInput.trim() && (
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: 'block', marginBottom: 6 }}
                >
                  Suggestions for &quot;
                  {autoSearchQuery.slice(0, 50)}
                  {autoSearchQuery.length > 50 ? '…' : ''}
                  &quot;
                </Text>
              )}

              {showSuggestions && (
                <div style={{
                  maxHeight: 280,
                  overflowY: 'auto',
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                }}
                >
                  {displaySuggestions.map((product, index) => {
                    const score = product.matchScore ?? 1;
                    const { label: confidenceLabel, color } = getConfidenceLabel(score);
                    const pct = Math.round((1 - Math.min(score, 1)) * 100);
                    const rowSku = getProductSku(product);
                    return (
                      <div
                        key={rowSku || getProductDearId(product) || index}
                        style={rowWrap}
                      >
                        <div style={{ ...rowInner, padding: '0 12px' }}>
                          <div style={mainBlock}>
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: 6,
                            }}
                            >
                              <Text style={{ fontSize: 13, wordBreak: 'break-word' }}>
                                {product.name || '—'}
                              </Text>
                              <Tag style={{ fontSize: 11 }}>
                                {rowSku || '—'}
                              </Tag>
                            </div>
                            <div style={{
                              fontSize: 11,
                              marginTop: 2,
                              color,
                            }}
                            >
                              {confidenceLabel}
                              {' '}
                              (
                              {pct}
                              %)
                            </div>
                          </div>
                          <Button
                            size="small"
                            type="primary"
                            onClick={() => handleSelect(product)}
                            style={{ flexShrink: 0 }}
                          >
                            Select
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!showSuggestions && !loadingCache && (
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: 'block', marginTop: 4 }}
                >
                  No suggestions found. Try searching above.
                </Text>
              )}
            </>
          )}
        </>
      )}

      {lastSyncedAt && (
        <div
          style={{
            fontSize: 12,
            color: 'rgba(0, 0, 0, 0.45)',
            marginTop: 8,
          }}
        >
          Cache synced:
          {' '}
          {formatRelativeTime(lastSyncedAt)}
        </div>
      )}
    </div>
  );
};

Cin7SkuMatcher.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSelectMatch: PropTypes.func,
  parentProductName: PropTypes.string,
  matrixSkuName: PropTypes.string,
  containerType: PropTypes.string,
  unitType: PropTypes.string,
  packType: PropTypes.string,
  caseTypeLabel: PropTypes.string,
  unitTypeLabel: PropTypes.string,
  compact: PropTypes.bool,
  searchOnly: PropTypes.bool,
  sharedCachedProducts: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.oneOf([null]),
  ]),
};

Cin7SkuMatcher.defaultProps = {
  value: '',
  onChange: () => {},
  onSelectMatch: () => {},
  parentProductName: '',
  matrixSkuName: '',
  containerType: '',
  unitType: '',
  packType: '',
  caseTypeLabel: '',
  unitTypeLabel: '',
  compact: false,
  searchOnly: false,
  sharedCachedProducts: undefined,
};

export default Cin7SkuMatcher;
